import { forwardRef } from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Thin semantic wrappers over the shadcn Button so screens stop using
 * inline `className="bg-destructive..."` overrides. Preserves asChild,
 * refs, sizes, and accessibility.
 */

export const PrimaryButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, "variant">>(
  ({ className, ...rest }, ref) => (
    <Button
      ref={ref}
      variant="default"
      className={cn("bg-[var(--roava-blue)] text-[var(--roava-blue-foreground)] hover:opacity-90", className)}
      {...rest}
    />
  ),
);
PrimaryButton.displayName = "PrimaryButton";

export const SecondaryButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, "variant">>(
  ({ className, ...rest }, ref) => (
    <Button
      ref={ref}
      variant="outline"
      className={cn("border-[var(--roava-border)] text-[var(--roava-text)]", className)}
      {...rest}
    />
  ),
);
SecondaryButton.displayName = "SecondaryButton";

export const DangerButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, "variant">>(
  ({ className, ...rest }, ref) => (
    <Button
      ref={ref}
      variant="default"
      className={cn("bg-[var(--roava-red)] text-[var(--roava-red-foreground)] hover:opacity-90", className)}
      {...rest}
    />
  ),
);
DangerButton.displayName = "DangerButton";

export const TextButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, "variant"> & { tone?: "blue" | "muted" }>(
  ({ className, tone = "blue", ...rest }, ref) => (
    <Button
      ref={ref}
      variant="link"
      className={cn(
        "px-0 h-auto",
        tone === "blue"
          ? "text-[var(--roava-blue)]"
          : "text-[var(--roava-text-muted)]",
        className,
      )}
      {...rest}
    />
  ),
);
TextButton.displayName = "TextButton";
